import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Heart, Footprints, Cloud, Sparkles } from "lucide-react";
import { GALLERY, GALLERY_SIZES, PRODUCT } from "@/config/product";
import { clarityEvent } from "@/lib/clarity";

const CAPTION_ICONS = { heart: Heart, footprints: Footprints, cloud: Cloud, sparkles: Sparkles };

// Tiny playful caption pill on gallery feature shots — 1-2 words + icon,
// slightly tilted, brand chip styling.
function CaptionChip({ caption, icon, idx }) {
  const Icon = CAPTION_ICONS[icon] || Heart;
  return (
    <span
      data-testid={`gallery-caption-${idx + 1}`}
      className="absolute left-3 bottom-3 z-10 inline-flex items-center gap-1.5 -rotate-2 rounded-full bg-ivory/95 backdrop-blur-sm border-2 border-ink shadow-[2px_2px_0px_#111111] pl-2 pr-3 py-1 font-display uppercase text-[11px] sm:text-xs tracking-[0.1em] text-ink"
    >
      <Icon className="w-3.5 h-3.5 text-[#FF6BAA]" strokeWidth={2.75} fill="currentColor" />
      {caption}
    </span>
  );
}

// PDP gallery — large full-width stage (shoe never cropped), translucent
// swipe arrows, a small "1 / 5" counter, and one row of thumbnails that
// switch the main image. Selecting a colour swaps the lead slide at
// identical dimensions and snaps back to it.
export default function PdpGallery({ colour }) {
  const trackRef = useRef(null);
  const rafRef = useRef(null);
  const [idx, setIdx] = useState(0);
  // First gallery interaction flips every slide to eager so fast swipers
  // never catch a frame still waiting on a lazy fetch.
  const [warm, setWarm] = useState(false);
  const warmUp = () => setWarm(true);
  const colourId = colour?.id || null;

  // Clarity: fires only when the shopper actually changes the main image
  // (arrow, thumbnail or swipe). Value-based guard, so StrictMode effect
  // re-runs and the initial paint never fire it.
  const prevIdx = useRef(idx);
  useEffect(() => {
    if (prevIdx.current === idx) return;
    prevIdx.current = idx;
    clarityEvent("ProductImageViewed");
  }, [idx]);

  const colourSrc = colour ? PRODUCT.colourImages?.[colour.id] : null;
  const slides = colourSrc
    ? [
        {
          shot: colour.name.toUpperCase(),
          src: colourSrc,
          alt: `POSSO Work Clog in ${colour.name}`,
          studio: true,
        },
        ...GALLERY.slice(1),
      ]
    : GALLERY;

  // Warm the 800w slide variants + colourway shots after page load so deep
  // swipes are instant — a fraction of the cost of warming the originals.
  useEffect(() => {
    const preload = () => {
      [
        ...GALLERY.map((s) => {
          if (!s.srcSet) return s.src;
          const entry =
            s.srcSet.split(",").map((e) => e.trim()).find((e) => e.endsWith("800w")) ||
            s.srcSet.split(",")[0].trim();
          return entry.split(" ")[0];
        }),
        ...Object.values(PRODUCT.colourImages || {}),
      ]
        .filter(Boolean)
        .forEach((src) => {
          const img = new Image();
          img.src = src;
        });
    };
    if (document.readyState === "complete") {
      preload();
      return;
    }
    window.addEventListener("load", preload, { once: true });
    return () => window.removeEventListener("load", preload);
  }, []);

  // Snap cleanly back to the lead slide when the colourway changes.
  useEffect(() => {
    const el = trackRef.current;
    if (el) el.scrollTo({ left: 0, behavior: "instant" });
    setIdx(0);
  }, [colourId]);

  // Throttle scroll-state updates to one per frame so swiping stays smooth.
  const onScroll = () => {
    if (rafRef.current) return;
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null;
      const el = trackRef.current;
      if (!el) return;
      setIdx(Math.round(el.scrollLeft / el.clientWidth));
    });
  };

  useEffect(
    () => () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    },
    []
  );

  const go = (i) => {
    const el = trackRef.current;
    if (el) el.scrollTo({ left: i * el.clientWidth, behavior: "smooth" });
  };

  const Slide = ({ s, i }) => {
    const eager = i === 0 || warm;
    return s.fit === "blur" ? (
      <div className="w-full aspect-[5/4] sm:aspect-[6/5] relative overflow-hidden">
        <img
          src={s.src}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover blur-2xl scale-125 brightness-90 select-none"
          loading={eager ? "eager" : "lazy"}
          decoding="async"
          draggable="false"
        />
        <img
          data-testid={`gallery-slide-${i + 1}`}
          src={s.src}
          srcSet={s.srcSet}
          sizes={s.srcSet ? GALLERY_SIZES : undefined}
          alt={s.alt}
          className="relative w-full h-full object-contain select-none"
          loading={eager ? "eager" : "lazy"}
          fetchPriority={i === 0 ? "high" : undefined}
          decoding="async"
          draggable="false"
        />
      </div>
    ) : (
      <img
        data-testid={`gallery-slide-${i + 1}`}
        src={s.src}
        srcSet={s.srcSet}
        sizes={s.srcSet ? GALLERY_SIZES : undefined}
        alt={s.alt}
        className={`w-full aspect-[5/4] sm:aspect-[6/5] select-none ${
          s.contain ? "object-contain bg-[#F2EDE4]" : "object-cover"
        }`}
        loading={eager ? "eager" : "lazy"}
        fetchPriority={i === 0 ? "high" : undefined}
        decoding="async"
        draggable="false"
        style={{ objectPosition: s.pos || "center" }}
      />
    );
  };

  return (
    <div data-testid="pdp-gallery" className="-mx-4 sm:mx-0">
      {/* Main stage — full-bleed on mobile */}
      <div className="relative">
        <div
          ref={trackRef}
          onScroll={onScroll}
          onPointerDown={warmUp}
          data-testid="gallery-track"
          data-lenis-prevent
          className="flex overflow-x-auto snap-x snap-mandatory no-scrollbar overscroll-x-contain border-t-2 border-ink sm:border-t-0 sm:rounded-3xl"
        >
          {slides.map((s, i) => (
            <div key={`${s.shot}-${i}`} className="relative w-full shrink-0 snap-center">
              <Slide s={s} i={i} />
              {s.caption && (
                <CaptionChip caption={s.caption} icon={s.icon} idx={i} />
              )}
            </div>
          ))}
        </div>
        <button
          data-testid="gallery-prev"
          aria-label="Previous image"
          onClick={() => { warmUp(); go(idx - 1); }}
          disabled={idx === 0}
          className="absolute left-2.5 top-1/2 -translate-y-1/2 z-10 w-9 h-9 rounded-full bg-ivory/60 backdrop-blur-sm flex items-center justify-center transition-opacity duration-150 disabled:opacity-25"
        >
          <ChevronLeft className="w-5 h-5 text-ink" strokeWidth={2.5} />
        </button>
        <button
          data-testid="gallery-next"
          aria-label="Next image"
          onClick={() => { warmUp(); go(idx + 1); }}
          disabled={idx === slides.length - 1}
          className="absolute right-2.5 top-1/2 -translate-y-1/2 z-10 w-9 h-9 rounded-full bg-ivory/60 backdrop-blur-sm flex items-center justify-center transition-opacity duration-150 disabled:opacity-25"
        >
          <ChevronRight className="w-5 h-5 text-ink" strokeWidth={2.5} />
        </button>
        <span
          data-testid="gallery-counter"
          className="absolute top-3 right-3 z-10 rounded-full bg-ivory/60 backdrop-blur-sm px-2.5 py-1 font-body font-bold text-[11px] tracking-[0.14em]"
        >
          {idx + 1} / {slides.length}
        </span>
      </div>

      {/* Thumbnail row — desktop only; mobile uses swipe + colour row below */}
      <div data-testid="gallery-thumbs" className="hidden sm:grid mt-2.5 grid-cols-5 gap-2 px-4 sm:px-0">
        {slides.map((s, i) => (
          <button
            key={`${s.shot}-thumb-${i}`}
            data-testid={`gallery-thumb-${i + 1}`}
            aria-label={`View ${s.shot}`}
            onClick={() => go(i)}
            className={`rounded-xl overflow-hidden transition-[box-shadow,opacity] duration-150 active:scale-95 ${
              i === idx ? "shadow-[0_0_0_2px_#111111]" : "opacity-60"
            }`}
          >
            <img
              src={s.src}
              alt=""
              className={`w-full aspect-[5/4] ${
                s.contain ? "object-contain bg-[#F2EDE4]" : "object-cover"
              }`}
              loading="lazy"
              draggable="false"
              style={{ objectPosition: s.pos || "center" }}
            />
          </button>
        ))}
      </div>
    </div>
  );
}
